import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import NotFound from "pages/NotFound";
import AccessDenied from "./pages/access-denied";
import Login from './pages/login';
import SignIn from './pages/sign-in';
import SignUp from './pages/sign-up';
import ServiceCalculator from './pages/service-calculator';
import MasterDataManagement from './pages/master-data-management';
import AnalyticsDashboard from './pages/analytics-dashboard';
import AccountManagement from './pages/account-management';
import AdminPanel from './pages/admin-panel';
import RolePermissionManagement from './pages/role-permission-management';
import AllIncentiveTickets from './pages/incentive-tickets';
import TicketDetails from './pages/incentive-tickets/TicketDetails';
import { PendingAssignmentTickets, PendingApprovalTickets, ApprovedTickets, ReadyForPaymentTickets, PaidTickets, RejectedTickets } from './pages/incentive-tickets/StatusPages';
import IncentiveReports from './pages/incentive-tickets/IncentiveReports';
import FinancePaymentReport from './pages/incentive-tickets/FinancePaymentReport';
import EmployeeIncentiveReport from './pages/incentive-tickets/EmployeeIncentiveReport';
import NotificationHistory from './pages/notification-history';
import AiTenderAnalyzer from './pages/tender-management/AiTenderAnalyzer';
import UploadedTenders from './pages/tender-management/UploadedTenders';
import ComplianceReports from './pages/tender-management/ComplianceReports';
import GeneratedTasks from './pages/tender-management/GeneratedTasks';
import TenderDashboard from './pages/tender-management/TenderDashboard';
import ExecutiveSnapshot from './pages/executive-snapshot';

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public routes */}
          <Route path="/" element={<Navigate to="/sign-in" replace />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/login" element={<Login />} />
          <Route path="/access-denied" element={<AccessDenied />} />

          {/* Protected routes - authenticated users */}
          <Route path="/service-calculator" element={
            <ProtectedRoute rbacModule="service_calculator">
              <ServiceCalculator />
            </ProtectedRoute>
          } />

          <Route path="/analytics-dashboard" element={
            <ProtectedRoute rbacModule="analytics_dashboard">
              <AnalyticsDashboard />
            </ProtectedRoute>
          } />

          <Route path="/executive-snapshot" element={
            <ProtectedRoute>
              <ExecutiveSnapshot />
            </ProtectedRoute>
          } />

          <Route path="/account-management" element={
            <ProtectedRoute>
              <AccountManagement />
            </ProtectedRoute>
          } />

          <Route path="/settings" element={<Navigate to="/account-management" replace />} />

          {/* Incentive Tickets routes */}
          <Route path="/incentive-tickets" element={
            <ProtectedRoute rbacModule="incentive_tickets">
              <AllIncentiveTickets />
            </ProtectedRoute>
          } />
          <Route path="/incentive-tickets/:id" element={
            <ProtectedRoute rbacModule="incentive_tickets">
              <TicketDetails />
            </ProtectedRoute>
          } />
          <Route path="/incentive-tickets/pending-assignment" element={
            <ProtectedRoute rbacModule="team_assignment">
              <PendingAssignmentTickets />
            </ProtectedRoute>
          } />
          <Route path="/incentive-tickets/pending-approval" element={
            <ProtectedRoute rbacModule="pending_approval">
              <PendingApprovalTickets />
            </ProtectedRoute>
          } />
          <Route path="/incentive-tickets/approved" element={
            <ProtectedRoute rbacModule="pending_approval">
              <ApprovedTickets />
            </ProtectedRoute>
          } />
          <Route path="/incentive-tickets/ready-for-payment" element={
            <ProtectedRoute rbacModule="finance_payments">
              <ReadyForPaymentTickets />
            </ProtectedRoute>
          } />
          <Route path="/incentive-tickets/paid" element={
            <ProtectedRoute rbacModule="finance_payments">
              <PaidTickets />
            </ProtectedRoute>
          } />
          <Route path="/incentive-tickets/rejected" element={
            <ProtectedRoute rbacModule="pending_approval">
              <RejectedTickets />
            </ProtectedRoute>
          } />
          <Route path="/incentive-tickets/reports" element={
            <ProtectedRoute rbacModule="analytics_dashboard">
              <IncentiveReports />
            </ProtectedRoute>
          } />
          <Route path="/incentive-tickets/finance-report" element={
            <ProtectedRoute rbacModule="finance_payments">
              <FinancePaymentReport />
            </ProtectedRoute>
          } />
          <Route path="/incentive-tickets/employee-report" element={
            <ProtectedRoute rbacModule="employee_incentive_reports">
              <EmployeeIncentiveReport />
            </ProtectedRoute>
          } />
          <Route path="/notification-history" element={
            <ProtectedRoute>
              <NotificationHistory />
            </ProtectedRoute>
          } />

          {/* Tender Management Routes */}
          <Route path="/tender-analyzer" element={
            <ProtectedRoute>
              <AiTenderAnalyzer />
            </ProtectedRoute>
          } />
          <Route path="/tender-uploaded" element={
            <ProtectedRoute>
              <UploadedTenders />
            </ProtectedRoute>
          } />
          <Route path="/tender-compliance" element={
            <ProtectedRoute>
              <UploadedTenders />
            </ProtectedRoute>
          } />
          <Route path="/tender-compliance/:id" element={
            <ProtectedRoute>
              <ComplianceReports />
            </ProtectedRoute>
          } />
          <Route path="/tender-tasks" element={
            <ProtectedRoute>
              <GeneratedTasks />
            </ProtectedRoute>
          } />
          <Route path="/tender-dashboard" element={
            <ProtectedRoute>
              <TenderDashboard />
            </ProtectedRoute>
          } />

          {/* Master Data */}
          <Route path="/master-data-management" element={
            <ProtectedRoute rbacModule="master_data">
              <MasterDataManagement />
            </ProtectedRoute>
          } />

          {/* Admin-only routes */}
          <Route path="/admin-panel" element={
            <ProtectedRoute rbacModule="users" rbacPermission="manage_users">
              <AdminPanel />
            </ProtectedRoute>
          } />
          <Route path="/role-permission-management" element={
            <ProtectedRoute rbacModule="roles_permissions" rbacPermission="manage_roles">
              <RolePermissionManagement />
            </ProtectedRoute>
          } />

          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
